import { useMemo } from 'react';
import {
  buildQuickNoteCategoryDecision,
  QUICK_TAG_CATEGORY_MAP,
} from '../utils/quickNoteClassification';
import { normalizeQuickCaptureTranscript } from '../utils/quickCaptureTranscriptNormalizer';
import { buildQuickCaptureStructureDecision } from '../utils/quickCaptureStructureDecision';
import {
  getQuickLogRoutingDecision,
  getQuickNoteCaptureState,
  shouldRouteShortEmailUpdateToCaptureFirst,
  shouldEscalateQuickStructuredReviewToAi,
} from '../utils/quickLogRoutingDecision';
import {
  detectNaturalCaptureInputType,
} from '../components/Dashboard/ImportLogs/importLogsPipeline';

const QUICK_CHECK_IN_NOTE_PLACEHOLDER = 'Type or dictate what happened…';

const buildReviewedQuickLogRoutingDecision = ({
  baseQuickLogRoutingDecision,
  quickCaptureNormalizedText,
  quickCaptureTranscript,
  quickCaptureStructureDecision,
  quickNoteCaptureState,
}) => {
  if (shouldRouteShortEmailUpdateToCaptureFirst({
    originalText: quickCaptureTranscript.originalText,
    routingDecision: baseQuickLogRoutingDecision,
  })) {
    const nextReason = 'short_email_update';
    const nextSignals = [
      ...new Set([
        ...(baseQuickLogRoutingDecision.routingPreview?.signals || []),
        'short_email_update',
      ]),
    ];

    return {
      ...baseQuickLogRoutingDecision,
      baseMode: baseQuickLogRoutingDecision.mode,
      finalMode: 'ai_smart_capture',
      longNotePreserved: false,
      mode: 'ai_smart_capture',
      reason: nextReason,
      localConfidence: 'low',
      inputType: detectNaturalCaptureInputType({
        source: 'quick_log',
        route: 'claude',
        hasStructuredEvents: true,
      }),
      shouldUseBackendExtraction: true,
      shouldReviewInApp: false,
      shouldSaveLocally: false,
      routingPreview: {
        ...baseQuickLogRoutingDecision.routingPreview,
        route: 'claude',
        reason: nextReason,
        signals: nextSignals,
        shouldUseBackendExtraction: true,
        baseMode: baseQuickLogRoutingDecision.mode,
        finalMode: 'ai_smart_capture',
        longNotePreserved: false,
      },
    };
  }

  if (baseQuickLogRoutingDecision.mode === 'long_note_review') {
    return {
      ...baseQuickLogRoutingDecision,
      baseMode: baseQuickLogRoutingDecision.mode,
      finalMode: baseQuickLogRoutingDecision.mode,
      longNotePreserved: true,
      routingPreview: {
        ...baseQuickLogRoutingDecision.routingPreview,
        baseMode: baseQuickLogRoutingDecision.mode,
        finalMode: baseQuickLogRoutingDecision.mode,
        longNotePreserved: true,
      },
    };
  }

  const shouldForceStructureReview = ['multiple_events', 'single_event_with_context', 'unclear']
    .includes(quickCaptureStructureDecision.structureType);
  const shouldForceTranscriptReview = quickCaptureTranscript.shouldForceReview;

  if (!shouldForceTranscriptReview && !shouldForceStructureReview) {
    return baseQuickLogRoutingDecision;
  }

  const structureReason = quickCaptureStructureDecision.structureType === 'multiple_events'
    ? 'structure_multiple_events'
    : quickCaptureStructureDecision.structureType === 'single_event_with_context'
      ? 'structure_add_context'
      : quickCaptureStructureDecision.structureType === 'unclear'
        ? 'structure_unclear'
        : 'normalized_transcript_review';
  const shouldEscalateToAi = shouldEscalateQuickStructuredReviewToAi({
    text: quickCaptureNormalizedText,
    transcriptNormalization: quickCaptureTranscript,
    structureDecision: quickCaptureStructureDecision,
    captureState: quickNoteCaptureState,
  });
  const nextMode = shouldEscalateToAi ? 'ai_smart_capture' : 'small_structured_review';
  const nextReason = shouldEscalateToAi ? 'local_split_low_quality' : structureReason;
  const nextRoute = shouldEscalateToAi ? 'claude' : 'review_needed';
  const nextSignals = [
    ...new Set([
      ...(baseQuickLogRoutingDecision.routingPreview?.signals || []),
      ...quickCaptureTranscript.reasons,
      ...(quickCaptureStructureDecision.reasons || []),
      ...(shouldEscalateToAi ? ['local_split_low_quality'] : []),
    ]),
  ];

  return {
    ...baseQuickLogRoutingDecision,
    baseMode: baseQuickLogRoutingDecision.mode,
    finalMode: nextMode,
    longNotePreserved: false,
    mode: nextMode,
    reason: nextReason,
    localConfidence: 'low',
    inputType: detectNaturalCaptureInputType({
      source: 'quick_log',
      route: nextRoute,
      hasStructuredEvents: !shouldEscalateToAi,
    }),
    shouldUseBackendExtraction: shouldEscalateToAi,
    shouldReviewInApp: !shouldEscalateToAi,
    shouldSaveLocally: false,
    routingPreview: {
      ...baseQuickLogRoutingDecision.routingPreview,
      route: nextRoute,
      reason: nextReason,
      signals: nextSignals,
      shouldUseBackendExtraction: shouldEscalateToAi,
      baseMode: baseQuickLogRoutingDecision.mode,
      finalMode: nextMode,
      longNotePreserved: false,
    },
  };
};

const resolveQuickCheckInCategory = ({
  autoCategory,
  overrideCategory,
  selectedTags,
}) => {
  const selectedTagCategory = selectedTags[0] ? QUICK_TAG_CATEGORY_MAP[selectedTags[0]] || null : null;
  return overrideCategory || selectedTagCategory || autoCategory;
};

const useQuickCheckInRoutingState = ({
  noteData = {},
  selectedTags = [],
  overrideCategory = null,
  showCapturePreviewSection = true,
} = {}) => {
  const noteText = noteData?.text || '';
  const quickCaptureTranscript = useMemo(
    () => normalizeQuickCaptureTranscript(noteText),
    [noteText]
  );
  const quickCaptureOriginalText = quickCaptureTranscript.originalText || noteText;
  const quickCaptureNormalizedText = quickCaptureTranscript.normalizedText || String(noteText || '').trim();
  const quickNoteCategoryDecision = useMemo(
    () => buildQuickNoteCategoryDecision(quickCaptureNormalizedText),
    [quickCaptureNormalizedText]
  );
  const autoCategory = quickNoteCategoryDecision.finalCategory || 'log';
  const baseQuickLogRoutingDecision = useMemo(
    () => getQuickLogRoutingDecision(quickCaptureNormalizedText),
    [quickCaptureNormalizedText]
  );
  const quickCaptureStructureDecision = useMemo(
    () => buildQuickCaptureStructureDecision({
      originalText: quickCaptureOriginalText,
      normalizedText: quickCaptureNormalizedText,
      categoryDecision: quickNoteCategoryDecision,
      routingDecision: baseQuickLogRoutingDecision,
      transcriptNormalization: quickCaptureTranscript,
    }),
    [
      quickCaptureOriginalText,
      quickCaptureNormalizedText,
      quickNoteCategoryDecision,
      baseQuickLogRoutingDecision,
      quickCaptureTranscript,
    ]
  );
  const quickNoteCaptureState = useMemo(
    () => getQuickNoteCaptureState(quickCaptureNormalizedText),
    [quickCaptureNormalizedText]
  );
  const quickLogRoutingDecision = useMemo(
    () => buildReviewedQuickLogRoutingDecision({
      baseQuickLogRoutingDecision,
      quickCaptureNormalizedText,
      quickCaptureTranscript,
      quickCaptureStructureDecision,
      quickNoteCaptureState,
    }),
    [
      baseQuickLogRoutingDecision,
      quickCaptureNormalizedText,
      quickCaptureTranscript,
      quickCaptureStructureDecision,
      quickNoteCaptureState,
    ]
  );
  const quickLogRoutingPreview = quickLogRoutingDecision.routingPreview;
  const resolvedCategory = useMemo(
    () => resolveQuickCheckInCategory({
      autoCategory,
      overrideCategory,
      selectedTags,
    }),
    [autoCategory, overrideCategory, selectedTags]
  );
  const detectedQuickNoteEntities = quickNoteCaptureState.entities;
  const detectedQuickNoteLabels = quickNoteCaptureState.labels;

  return {
    noteText,
    quickCaptureTranscript,
    quickCaptureOriginalText,
    quickCaptureNormalizedText,
    quickNoteCategoryDecision,
    autoCategory,
    baseQuickLogRoutingDecision,
    quickCaptureStructureDecision,
    quickNoteCaptureState,
    quickLogRoutingDecision,
    quickLogRoutingPreview,
    resolvedCategory,
    detectedQuickNoteEntities,
    detectedQuickNoteLabels,
    shouldShowFrontendCapturePreview: showCapturePreviewSection
      && quickLogRoutingDecision.mode === 'simple_local'
      && quickNoteCaptureState.hasCaptureableEntries,
    shouldShowBackendSmartCapturePreview: showCapturePreviewSection
      && quickLogRoutingDecision.mode === 'ai_smart_capture',
    shouldShowReviewNeededPreview: showCapturePreviewSection
      && (
        quickLogRoutingDecision.mode === 'small_structured_review'
        || quickLogRoutingDecision.mode === 'long_note_review'
      ),
    notePlaceholder: QUICK_CHECK_IN_NOTE_PLACEHOLDER,
    hasNoteText: Boolean(noteText.trim()),
  };
};

export default useQuickCheckInRoutingState;
